// src/pages/Notifications/NotificationsView.tsx
import React from 'react'
import Sidebar from '../../components/Layout/Sidebar'
import Header from '../../components/Layout/Header'
import Card from '../../components/UI/Card'
import Button from '../../components/UI/Button'
import {Bell, CheckCheck, Trash2, Info, AlertTriangle, CheckCircle} from 'lucide-react'
import {formatDate} from '../../utils/helpers'
import {Notification, FilterType} from './NotificationsModel'

interface NotificationsViewProps {
    sidebarOpen: boolean
    filter: FilterType
    notifications: Notification[]
    loading: boolean
    unreadCount: number
    total: number
    hasMore: boolean
    toggleSidebar: () => void
    handleFilterChange: (filter: FilterType) => void
    handleMarkAsRead: (id: number) => Promise<void>
    handleMarkAllAsRead: () => Promise<void>
    handleDelete: (id: number) => Promise<void>
    handleDeleteRead: () => Promise<void>
    loadMore: () => void
    getIcon: (type: string) => string
    getBorderColor: (type: string) => string
}

export const NotificationsView: React.FC<NotificationsViewProps> = ({
    sidebarOpen,
    filter,
    notifications,
    loading,
    unreadCount,
    total,
    hasMore,
    toggleSidebar,
    handleFilterChange,
    handleMarkAsRead,
    handleMarkAllAsRead,
    handleDelete,
    handleDeleteRead,
    loadMore,
    getIcon,
    getBorderColor
}) => {
    const renderIcon = (type: string) => {
        switch (type) {
            case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />
            case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500" />
            case 'info': return <Info className="w-5 h-5 text-blue-500" />
            default: return <Bell className="w-5 h-5 text-gray-500" />
        }
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Header toggleSidebar={toggleSidebar} />

            <Sidebar
                isOpen={sidebarOpen}
                onClose={() => toggleSidebar()}
                isMobile={true}
            />

            <div className="hidden lg:block">
                <Sidebar isMobile={false} />
            </div>

            <main className="pt-16 lg:pr-64">
                <div className="p-4 lg:p-6">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <Bell className="w-6 h-6 text-blue-600" />
                                {unreadCount > 0 && (
                                    <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                                        {unreadCount > 9 ? '9+' : unreadCount}
                                    </span>
                                )}
                            </div>
                            <div>
                                <h1 className="text-2xl lg:text-3xl font-bold text-gray-800">الإشعارات</h1>
                                <p className="text-sm text-gray-500">آخر التحديثات والأنشطة</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <select
                                value={filter}
                                onChange={(e) => handleFilterChange(e.target.value as FilterType)}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white"
                            >
                                <option value="all">كل الإشعارات</option>
                                <option value="unread">غير مقروء</option>
                            </select>

                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleMarkAllAsRead}
                                disabled={unreadCount === 0}
                                icon={<CheckCheck className="w-4 h-4" />}
                            >
                                تحديد الكل مقروء
                            </Button>

                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleDeleteRead}
                                disabled={unreadCount === total}
                                icon={<Trash2 className="w-4 h-4" />}
                            >
                                حذف المقروء
                            </Button>
                        </div>
                    </div>

                    {/* Notifications List */}
                    <Card>
                        {loading && !notifications.length ? (
                            <div className="text-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                                <p className="mt-2 text-gray-500">جاري التحميل...</p>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="text-center py-12">
                                <Bell className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                <p className="text-gray-500 text-lg">لا توجد إشعارات</p>
                                <p className="text-sm text-gray-400 mt-1">
                                    {filter === 'unread' ? 'لا توجد إشعارات غير مقروءة' : 'سوف تظهر هنا الإشعارات الجديدة'}
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="divide-y divide-gray-200">
                                    {notifications.map(notif => (
                                        <div
                                            key={notif.id}
                                            className={`p-4 hover:bg-gray-50 transition-colors ${notif.is_read === 0 ? 'bg-blue-50/50' : ''
                                                }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className="mt-1">{renderIcon(notif.type)}</div>
                                                <div className="flex-1">
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div>
                                                            <h3 className="font-medium text-gray-900">{notif.title}</h3>
                                                            <p className="text-sm text-gray-600 mt-1">{notif.message}</p>
                                                            <p className="text-xs text-gray-400 mt-2">{formatDate(notif.created_at)}</p>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            {notif.is_read === 0 && (
                                                                <button
                                                                    onClick={() => handleMarkAsRead(notif.id)}
                                                                    className="p-1 hover:bg-blue-100 rounded-lg transition-colors"
                                                                    title="تحديد كمقروء"
                                                                >
                                                                    <CheckCheck className="w-4 h-4 text-blue-600" />
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => handleDelete(notif.id)}
                                                                className="p-1 hover:bg-red-100 rounded-lg transition-colors"
                                                                title="حذف"
                                                            >
                                                                <Trash2 className="w-4 h-4 text-red-600" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {hasMore && (
                                    <div className="p-4 border-t border-gray-200 text-center">
                                        <Button
                                            variant="outline"
                                            onClick={loadMore}
                                            loading={loading}
                                        >
                                            تحميل المزيد
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </Card>
                </div>
            </main>
        </div>
    )
}